import React, { Component, ErrorInfo, ReactNode } from "react";
import { Translation } from "react-i18next";
import { createLogger } from "../../utils/logger";

const logger = createLogger("FirebaseErrorBoundary");

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isFirebaseError: boolean;
}

/**
 * Firebase-specific error boundary
 * Handles Firebase initialization and App Check errors gracefully
 */
class FirebaseErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isFirebaseError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    const isFirebaseError =
      error.message.includes("Firebase") ||
      error.message.includes("App Check") ||
      error.message.includes("VITE_RECAPTCHA_SITE_KEY");

    return {
      hasError: true,
      error,
      isFirebaseError,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Firebase Error Boundary caught error:", error, errorInfo);

    // Report Firebase errors for monitoring
    if (this.state.isFirebaseError) {
      logger.error("[FirebaseErrorBoundary] Firebase service error:", {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, isFirebaseError: false });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Custom Firebase error UI
      if (this.state.isFirebaseError) {
        return (
          <Translation>
            {(t) => (
              <div
                style={{
                  padding: "var(--spacing-scale-400)",
                  textAlign: "center",
                  backgroundColor: "var(--color-background-secondary)",
                  border: "1px solid var(--color-border-error)",
                  borderRadius: "var(--radius-m)",
                  margin: "var(--spacing-scale-400)",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                }}
              >
                <h2
                  style={{
                    color: "var(--color-text-error)",
                    marginBottom: "var(--spacing-scale-200)",
                  }}
                >
                  {t("error_boundary.firebase_title")}
                </h2>
                <p
                  style={{
                    color: "var(--color-text-secondary)",
                    marginBottom: "var(--spacing-scale-300)",
                  }}
                >
                  {t("error_boundary.firebase_body")}
                </p>

                {import.meta.env.DEV && (
                  <details
                    style={{
                      marginBottom: "var(--spacing-scale-300)",
                      textAlign: "left",
                      backgroundColor: "var(--color-background-primary)",
                      padding: "var(--spacing-scale-200)",
                      borderRadius: "var(--radius-s)",
                    }}
                  >
                    <summary style={{ cursor: "pointer", fontWeight: "500" }}>
                      {t("error_boundary.development_info")}
                    </summary>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: "0.875rem",
                        color: "var(--color-text-primary)",
                        marginTop: "var(--spacing-scale-100)",
                      }}
                    >
                      {this.state.error?.message}
                      {this.state.error?.stack}
                    </pre>
                  </details>
                )}

                <button
                  onClick={this.handleRetry}
                  style={{
                    backgroundColor: "var(--color-interactive-primary-default)",
                    color: "var(--color-text-on-primary)",
                    border: "none",
                    padding:
                      "var(--spacing-scale-200) var(--spacing-scale-300)",
                    borderRadius: "var(--radius-s)",
                    cursor: "pointer",
                    fontWeight: "500",
                    marginRight: "var(--spacing-scale-200)",
                  }}
                >
                  {t("common.retry")}
                </button>

                <button
                  onClick={() => window.location.reload()}
                  style={{
                    backgroundColor:
                      "var(--color-interactive-secondary-default)",
                    color: "var(--color-text-secondary)",
                    border: "1px solid var(--color-border-default)",
                    padding:
                      "var(--spacing-scale-200) var(--spacing-scale-300)",
                    borderRadius: "var(--radius-s)",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  {t("error_boundary.refresh_page")}
                </button>
              </div>
            )}
          </Translation>
        );
      }

      // Generic error fallback
      return (
        <Translation>
          {(t) => (
            <div
              style={{
                padding: "var(--spacing-scale-400)",
                textAlign: "center",
                border: "1px solid var(--color-border-error)",
                margin: "var(--spacing-scale-400)",
                borderRadius: "var(--radius-m)",
              }}
            >
              <h2 style={{ color: "var(--color-text-error)" }}>
                {t("error_boundary.generic_title")}
              </h2>
              <p style={{ color: "var(--color-text-secondary)" }}>
                {t("error_boundary.generic_body_short")}
              </p>
              <button
                onClick={this.handleRetry}
                style={{
                  backgroundColor: "var(--color-interactive-primary-default)",
                  color: "var(--color-text-on-primary)",
                  border: "none",
                  padding: "var(--spacing-scale-200) var(--spacing-scale-300)",
                  borderRadius: "var(--radius-s)",
                  cursor: "pointer",
                  marginTop: "var(--spacing-scale-200)",
                }}
              >
                {t("error_boundary.try_again")}
              </button>
            </div>
          )}
        </Translation>
      );
    }

    return this.props.children;
  }
}

export default FirebaseErrorBoundary;
